
import { Input } from "@/components/ui/Input";
import { InputShell } from "@/components/ui/InputShell";
import { Select } from "@/components/ui/Select";
import { Slider } from "@/components/ui/Slider";
import { useCallback } from "react";
import type { LightSource, LightType } from "@mind-sands/shared";
import { LIGHT_TYPES } from "@mind-sands/shared";

/** Valid ranges for light properties */
const RANGES = {
  intensity: { min: 0, max: 3 },
  position: { min: -100, max: 100 },
  distance: { min: 0, max: 100 },
  decay: { min: 1, max: 5 },
  angle: { min: 0, max: 90 },
  penumbra: { min: 0, max: 1 },
} as const;

/** Clamp a number to [min, max] */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Light type options for dropdown */
const LIGHT_TYPE_OPTIONS: { value: LightType; label: string }[] = [
  { value: LIGHT_TYPES.DIRECTIONAL, label: "Directional (Sun/Moon)" },
  { value: LIGHT_TYPES.POINT, label: "Point (Bulb/Candle)" },
  { value: LIGHT_TYPES.SPOT, label: "Spot (Flashlight/Stage)" },
];

interface LightEditorProps {
  light: LightSource;
  onChange: (updated: LightSource) => void;
  onDelete: () => void;
}

/**
 * Editor for a single light source.
 * Controls are grouped by light type to avoid clutter.
 */
export function LightEditor({ light, onChange, onDelete }: LightEditorProps) {
  const handleTypeChange = useCallback(
    (value: string) => {
      onChange({ ...light, type: value as LightType });
    },
    [light, onChange],
  );

  const handleColorChange = useCallback(
    (color: string) => {
      onChange({ ...light, color });
    },
    [light, onChange],
  );

  const handleIntensityChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;
      const clamped = clamp(parsed, RANGES.intensity.min, RANGES.intensity.max);
      onChange({ ...light, intensity: clamped });
    },
    [light, onChange],
  );

  const handlePositionChange = useCallback(
    (axis: "x" | "y" | "z", rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;
      const clamped = clamp(parsed, RANGES.position.min, RANGES.position.max);
      const currentPos = light.position ?? [0, 10, 0];
      const newPos: [number, number, number] = [...currentPos];
      if (axis === "x") newPos[0] = clamped;
      if (axis === "y") newPos[1] = clamped;
      if (axis === "z") newPos[2] = clamped;
      onChange({ ...light, position: newPos });
    },
    [light, onChange],
  );

  const handleShadowToggle = useCallback(() => {
    onChange({ ...light, castShadow: !light.castShadow });
  }, [light, onChange]);

  // Point light specific
  const handleDistanceChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;
      const clamped = clamp(parsed, RANGES.distance.min, RANGES.distance.max);
      onChange({ ...light, distance: clamped });
    },
    [light, onChange],
  );

  const handleDecayChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;
      const clamped = clamp(parsed, RANGES.decay.min, RANGES.decay.max);
      onChange({ ...light, decay: clamped });
    },
    [light, onChange],
  );

  // Spot light specific
  const handleAngleChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;
      const clamped = clamp(parsed, RANGES.angle.min, RANGES.angle.max);
      onChange({ ...light, angle: clamped });
    },
    [light, onChange],
  );

  const handlePenumbraChange = useCallback(
    (rawValue: string) => {
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed)) return;
      const clamped = clamp(parsed, RANGES.penumbra.min, RANGES.penumbra.max);
      onChange({ ...light, penumbra: clamped });
    },
    [light, onChange],
  );

  return (
    <div className="mb-3 p-3 bg-sands-50 border border-sands-200 rounded-lg">
      {/* Header: ID + Type + Delete */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-medium text-sands-600 flex-1 truncate">
          {light.id}
        </span>
        <Select
          variant="inline"
          value={light.type}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          {LIGHT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 text-xs px-2 py-1"
          title="Delete light"
        >
          ×
        </button>
      </div>

      {/* Color */}
      <div className="mb-2">
        <label className="block text-[10px] font-medium text-sands-500 uppercase tracking-wider mb-1">
          Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={light.color}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-8 h-8 rounded border border-sands-200 cursor-pointer"
          />
          <Input
            type="text"
            value={light.color}
            onChange={(e) => handleColorChange(e.target.value)}
            accent="healing"
            borderWeight="thin"
            inputSize="xs"
            mono
            className="flex-1"
          />
        </div>
      </div>

      {/* Intensity */}
      <div className="mb-2">
        <label className="block text-[10px] font-medium text-sands-500 uppercase tracking-wider mb-1">
          Intensity
        </label>
        <div className="flex items-center gap-2">
          <Slider
            min={RANGES.intensity.min}
            max={RANGES.intensity.max}
            step="0.05"
            value={light.intensity}
            onChange={(e) => handleIntensityChange(e.target.value)}
          />
          <span className="text-xs text-sands-500 w-10 text-right">
            {light.intensity.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Position (all types) */}
      <div className="mb-2">
        <label className="block text-[10px] font-medium text-sands-500 uppercase tracking-wider mb-1">
          Position
        </label>
        <div className="grid grid-cols-3 gap-1.5">
          {(["x", "y", "z"] as const).map((axis) => (
            <InputShell key={axis} className="bg-white px-2 py-1">
              <span className="text-[10px] text-sands-400 font-medium uppercase">
                {axis}
              </span>
              <Input
                type="number"
                step="1"
                value={
                  light.position?.[axis === "x" ? 0 : axis === "y" ? 1 : 2] ?? 0
                }
                onChange={(e) => handlePositionChange(axis, e.target.value)}
                accent="none"
                surface="ghost"
                borderWeight="none"
                inputSize="xs"
                className="flex-1 min-w-0 text-xs"
              />
            </InputShell>
          ))}
        </div>
      </div>

      {/* Shadow toggle */}
      <div className="mb-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={light.castShadow ?? false}
            onChange={handleShadowToggle}
            className="w-4 h-4 accent-healing-500"
          />
          <span className="text-[10px] font-medium text-sands-500 uppercase tracking-wider">
            Cast Shadow
          </span>
        </label>
      </div>

      {/* Point light specific: Distance + Decay */}
      {light.type === LIGHT_TYPES.POINT && (
        <>
          <div className="mb-2">
            <label className="block text-[10px] font-medium text-sands-500 uppercase tracking-wider mb-1">
              Distance (0 = infinite)
            </label>
            <Input
              type="number"
              step="1"
              min={RANGES.distance.min}
              max={RANGES.distance.max}
              value={light.distance ?? 0}
              onChange={(e) => handleDistanceChange(e.target.value)}
              accent="healing"
              borderWeight="thin"
              inputSize="xs"
            />
          </div>

          <div className="mb-2">
            <label className="block text-[10px] font-medium text-sands-500 uppercase tracking-wider mb-1">
              Decay
            </label>
            <Input
              type="number"
              step="0.1"
              min={RANGES.decay.min}
              max={RANGES.decay.max}
              value={light.decay ?? 2}
              onChange={(e) => handleDecayChange(e.target.value)}
              accent="healing"
              borderWeight="thin"
              inputSize="xs"
            />
          </div>
        </>
      )}

      {/* Spot light specific: Angle + Penumbra */}
      {light.type === LIGHT_TYPES.SPOT && (
        <>
          <div className="mb-2">
            <label className="block text-[10px] font-medium text-sands-500 uppercase tracking-wider mb-1">
              Angle (degrees)
            </label>
            <Input
              type="number"
              step="1"
              min={RANGES.angle.min}
              max={RANGES.angle.max}
              value={light.angle ?? 30}
              onChange={(e) => handleAngleChange(e.target.value)}
              accent="healing"
              borderWeight="thin"
              inputSize="xs"
            />
          </div>

          <div className="mb-2">
            <label className="block text-[10px] font-medium text-sands-500 uppercase tracking-wider mb-1">
              Penumbra (edge softness)
            </label>
            <Slider
              min={RANGES.penumbra.min}
              max={RANGES.penumbra.max}
              step="0.05"
              value={light.penumbra ?? 0}
              onChange={(e) => handlePenumbraChange(e.target.value)}
              className="w-full"
            />
          </div>
        </>
      )}
    </div>
  );
}
